import { IDentalData } from '@dataTypes/chartingFeature.types'
import {
  MOLAR_TOOTH_IDS,
  PREMOLAR_TOOTH_IDS,
  INCISOR_CANINE_TOOTH_IDS,
  LOWER_MOLAR_TOOTH_IDS,
  LOWER_PREMOLAR_TOOTH_IDS,
  LOWER_INCISOR_CANINE_TOOTH_IDS,
} from '@constants/Charting/conditions.schema'

const isAnteriorFacialTooth = (toothNumber: number): boolean => {
  return (
    (toothNumber >= 6 && toothNumber <= 11) ||
    (toothNumber >= 22 && toothNumber <= 27)
  )
}

const normalizeLabialSurfaceLabel = (
  label: string,
  toothNumber: number,
): string => {
  const useFacial = isAnteriorFacialTooth(toothNumber)
  const toBuccalLabel: Record<string, string> = {
    Facial: 'Buccal',
    'Facial Cusp': 'Buccal Cusp',
    'Facial Surface': 'Buccal Surface',
    'Cervical Facial': 'Cervical Buccal',
    'Mesio Facial Cusp': 'Mesio Buccal Cusp',
    'Disto Facial Cusp': 'Disto Buccal Cusp',
  }
  const toFacialLabel: Record<string, string> = {
    Buccal: 'Facial',
    'Buccal Cusp': 'Facial Cusp',
    'Buccal Surface': 'Facial Surface',
    'Cervical Buccal': 'Cervical Facial',
    'Mesio Buccal Cusp': 'Mesio Facial Cusp',
    'Disto Buccal Cusp': 'Disto Facial Cusp',
  }

  if (useFacial) {
    return toFacialLabel[label] ?? label
  }

  return toBuccalLabel[label] ?? label
}

/**
 * Get surface rows for a specific tooth type
 */
export const getSurfaceRows = (
  selectedTooth: number | null,
  currentConditions: IDentalData[] = [],
): string[][] => {
  // Upper molars & premolars (1-3,14-16 and 4,5,12,13)
  const molarRows = [
    ['Mesio Facial Cusp', 'Facial', 'Disto Facial Cusp'],
    ['Mesial', 'Occlusal', 'Distal'],
    ['Mesio Palatal Cusp', 'Palatal', 'Disto Palatal Cusp'],
  ]

  // Incisors and canines (teeth 6,7,8,9,10,11)
  const incisorCanineRows = [
    ['Facial', 'Palatal'],
    ['Mesial', 'Incisal', 'Distal'],
    ['MIFP', 'DIFP'],
  ]

  // Lower molars & premolars (17-19,30-32 and 20,21,28,29)
  const lowerMolarRows = [
    ['Mesio Facial Cusp', 'Facial', 'Disto Facial Cusp'],
    ['Mesial', 'Occlusal', 'Distal'],
    ['Mesio Lingual Cusp', 'Lingual', 'Disto Lingual Cusp'],
  ]

  // Lower incisors and canines (teeth 22,23,24,25,26,27)
  const lowerIncisorCanineRows = [
    ['Facial', 'Lingual'],
    ['Mesial', 'Incisal', 'Distal'],
    ['MIFL', 'DIFL'],
  ]

  if (!selectedTooth) return molarRows

  const normalizeRowsForTooth = (rows: string[][]): string[][] => {
    return rows.map((row) =>
      row.map((label) => normalizeLabialSurfaceLabel(label, selectedTooth)),
    )
  }

  if (
    MOLAR_TOOTH_IDS.includes(selectedTooth) ||
    PREMOLAR_TOOTH_IDS.includes(selectedTooth)
  ) {
    return normalizeRowsForTooth(molarRows)
  }

  if (INCISOR_CANINE_TOOTH_IDS.includes(selectedTooth)) {
    return normalizeRowsForTooth(incisorCanineRows)
  }

  if (
    LOWER_MOLAR_TOOTH_IDS.includes(selectedTooth) ||
    LOWER_PREMOLAR_TOOTH_IDS.includes(selectedTooth)
  ) {
    return normalizeRowsForTooth(lowerMolarRows)
  }

  if (LOWER_INCISOR_CANINE_TOOTH_IDS.includes(selectedTooth)) {
    return normalizeRowsForTooth(lowerIncisorCanineRows)
  }

  return normalizeRowsForTooth(molarRows)
}

/**
 * Check if surface is disabled for erosion condition
 */
export const isSurfaceDisabledForErosion = (
  surface: string,
  selectedTooth: number | null,
  hasErosion: boolean,
  currentConditions: IDentalData[] = [],
): boolean => {
  if (!selectedTooth) return false

  // Only allow Facial and Palatal/Lingual surfaces for erosion
  // For upper teeth (1-16), allow Facial and Palatal
  if (selectedTooth >= 1 && selectedTooth <= 16) {
    return !['Facial', 'Palatal'].includes(surface)
  }

  // For lower teeth (17-32), allow Facial and Lingual
  if (selectedTooth >= 17 && selectedTooth <= 32) {
    return !['Facial', 'Lingual'].includes(surface)
  }

  return true // Disable all surfaces if tooth is invalid
}

/**
 * Check if surface is disabled for filling condition
 */
export const isSurfaceDisabledForFilling = (
  surface: string,
  selectedTooth: number | null,
  hasFilling: boolean,
): boolean => {
  // Filling surfaces should not be disabled - allow all surfaces for filling
  return false
}

/**
 * Check if surface is disabled for crown condition
 */
export const isSurfaceDisabledForCrown = (hasCrown: boolean): boolean => {
  // When crown condition is active, all surfaces should be disabled
  // since crowns cover the entire tooth and don't need specific surface selection
  return hasCrown
}

export const wearSurfaceFromGridCode = (surfaceCode: string): string => {
  if (surfaceCode === 'p') return 'palatal'
  if (surfaceCode === 'l') return 'lingual'
  if (surfaceCode === 'b') return 'buccal'
  if (surfaceCode === 'o' || surfaceCode === 'i') return 'occlusal'
  return surfaceCode
}

export const storedWearSurfaceMatches = (
  storedSurface: string | null | undefined,
  wearSurface: string,
): boolean => {
  if (!storedSurface) {
    return false
  }
  const stored = storedSurface.toLowerCase()
  if (wearSurface === 'occlusal') {
    return stored === 'occlusal' || stored === 'o'
  }
  if (wearSurface === 'buccal') {
    return stored === 'buccal' || stored === 'b'
  }
  if (wearSurface === 'palatal') {
    return stored === 'palatal' || stored === 'p'
  }
  if (wearSurface === 'lingual') {
    return stored === 'lingual' || stored === 'l'
  }
  return stored === wearSurface.toLowerCase()
}

export const getAbrasionSurfaceRows = (
  selectedTooth: number | null,
): string[][] => {
  const labialLabel =
    selectedTooth !== null && isAnteriorFacialTooth(selectedTooth)
      ? 'Facial'
      : 'Buccal'
  if (selectedTooth === null) {
    return [[labialLabel, 'Palatal'], ['Lingual']]
  }
  if (selectedTooth >= 1 && selectedTooth <= 16) {
    return [[labialLabel, 'Palatal']]
  }
  return [['Lingual', labialLabel]]
}

/**
 * Check if surface is disabled for abrasion condition
 */
export const isSurfaceDisabledForAbrasion = (
  surface: string,
  selectedTooth: number | null,
): boolean => {
  if (!selectedTooth) return false
  if (selectedTooth >= 1 && selectedTooth <= 16) {
    return !['Facial', 'Buccal', 'Palatal'].includes(surface)
  }
  if (selectedTooth >= 17 && selectedTooth <= 32) {
    return !['Facial', 'Buccal', 'Lingual'].includes(surface)
  }
  return true
}

/**
 * Check if surface is disabled for apicoectomy condition
 */
export const isSurfaceDisabledForApicoectomy = (
  hasApicoectomy: boolean,
): boolean => {
  // When apicoectomy condition is active, all surfaces should be disabled
  // since apicoectomy is a root-end surgery that doesn't need specific surface selection
  return hasApicoectomy
}

/**
 * Check if surface is disabled for periapical condition
 */
export const isSurfaceDisabledForPeriapical = (
  hasPeriapical: boolean,
): boolean => {
  // When periapical condition is active, all surfaces should be disabled
  // since periapical refers to conditions around the root tip and doesn't need specific surface selection
  return hasPeriapical
}

/**
 * Check if surface is disabled for periradicular condition
 */
export const isSurfaceDisabledForPeriradicular = (
  hasPeriradicular: boolean,
): boolean => {
  // When periradicular condition is active, all surfaces should be disabled
  // since periradicular refers to conditions around the root area and doesn't need specific surface selection
  return hasPeriradicular
}

/**
 * Check if surface is disabled for pulpotomy condition
 */
export const isSurfaceDisabledForPulpotomy = (
  hasPulpotomy: boolean,
): boolean => {
  // When pulpotomy condition is active, all surfaces should be disabled
  // since pulpotomy refers to a pulp chamber procedure and doesn't need specific surface selection
  return hasPulpotomy
}

/**
 * Check if surface is disabled for pulpectomy condition
 */
export const isSurfaceDisabledForPulpectomy = (
  hasPulpectomy: boolean,
): boolean => {
  // When pulpectomy condition is active, all surfaces should be disabled
  // since pulpectomy refers to pulp tissue removal and doesn't need specific surface selection
  return hasPulpectomy
}

/**
 * Check if surface is disabled for sinus tract condition
 */
export const isSurfaceDisabledForSinusTract = (
  hasSinusTract: boolean,
): boolean => {
  // When sinus tract condition is active, all surfaces should be disabled
  // since sinus tract refers to conditions around the root area and doesn't need specific surface selection
  return hasSinusTract
}
